import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
	Form,
	FormControl,
	FormDescription,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/common/ui/form'
import { Button } from '@/common/ui/button'
import { Switch } from '@/common/ui/switch'
import { TimePicker } from '@/common/components/TimePicker'
import type {
	TransactionSchedule,
	CreateTransactionScheduleDto,
} from '../../types'

const scheduleSchema = z
	.object({
		inactiveStartAt: z
			.string()
			.regex(/^([01]?\d|2[0-3]):([0-5]\d)$/, 'รูปแบบเวลาไม่ถูกต้อง (HH:mm)'),
		inactiveEndAt: z
			.string()
			.regex(/^([01]?\d|2[0-3]):([0-5]\d)$/, 'รูปแบบเวลาไม่ถูกต้อง (HH:mm)'),
		isActive: z.boolean().default(true),
	})
	.refine((data) => data.inactiveStartAt !== data.inactiveEndAt, {
		message: 'เวลาเริ่มต้นและสิ้นสุดต้องไม่เท่ากัน',
		path: ['inactiveEndAt'],
	})

type ScheduleFormValues = z.infer<typeof scheduleSchema>

interface ScheduleFormProps {
	initialData?: TransactionSchedule
	onSubmit: (data: CreateTransactionScheduleDto) => void
	isLoading?: boolean
	onCancel?: () => void
}

/**
 * Convert local time (HH:mm) to UTC time string
 * Uses browser's timezone automatically
 */
const localToUtc = (localTime: string): string => {
	const [hours, minutes] = localTime.split(':').map(Number)

	// Create a date with local time
	const localDate = new Date()
	localDate.setHours(hours, minutes, 0, 0)

	// Get UTC hours/minutes from the same moment
	const utcHours = localDate.getUTCHours()
	const utcMinutes = localDate.getUTCMinutes()

	return `${utcHours.toString().padStart(2, '0')}:${utcMinutes.toString().padStart(2, '0')}`
}

/**
 * Convert UTC time string to local time (HH:mm)
 * Uses browser's timezone automatically
 */
const utcToLocal = (utcTime: string): string => {
	const [hours, minutes] = utcTime.split(':').map(Number)

	// Create a date in UTC
	const utcDate = new Date()
	utcDate.setUTCHours(hours, minutes, 0, 0)

	// Get local hours/minutes from the same moment
	const localHours = utcDate.getHours()
	const localMinutes = utcDate.getMinutes()

	return `${localHours.toString().padStart(2, '0')}:${localMinutes.toString().padStart(2, '0')}`
}

/**
 * Get user's timezone name for display
 */
const getUserTimezone = (): string => {
	return Intl.DateTimeFormat().resolvedOptions().timeZone
}

export const ScheduleForm = ({
	initialData,
	onSubmit,
	isLoading,
	onCancel,
}: ScheduleFormProps) => {
	const userTimezone = getUserTimezone()

	const form = useForm<ScheduleFormValues>({
		resolver: zodResolver(scheduleSchema),
		defaultValues: {
			inactiveStartAt: initialData
				? utcToLocal(initialData.inactiveStartAt)
				: '',
			inactiveEndAt: initialData ? utcToLocal(initialData.inactiveEndAt) : '',
			isActive: initialData?.isActive ?? true,
		},
	})

	const handleSubmit = (values: ScheduleFormValues) => {
		// Convert local time to UTC before sending to API
		onSubmit({
			inactiveStartAt: localToUtc(values.inactiveStartAt),
			inactiveEndAt: localToUtc(values.inactiveEndAt),
			isActive: values.isActive,
		})
	}

	return (
		<Form {...form}>
			<form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
				<div className="grid gap-6 md:grid-cols-2">
					<FormField
						control={form.control}
						name="inactiveStartAt"
						render={({ field }) => (
							<FormItem>
								<FormLabel>เวลาเริ่มปิด ({userTimezone})</FormLabel>
								<FormControl>
									<TimePicker
										value={field.value}
										onChange={field.onChange}
										placeholder="เลือกเวลาเริ่มปิด"
									/>
								</FormControl>
								<FormDescription>เวลาที่จะเริ่มปิดระบบทุกวัน</FormDescription>
								<FormMessage />
							</FormItem>
						)}
					/>

					<FormField
						control={form.control}
						name="inactiveEndAt"
						render={({ field }) => (
							<FormItem>
								<FormLabel>เวลาเปิด ({userTimezone})</FormLabel>
								<FormControl>
									<TimePicker
										value={field.value}
										onChange={field.onChange}
										placeholder="เลือกเวลาเปิด"
									/>
								</FormControl>
								<FormDescription>
									เวลาที่จะเปิดระบบอีกครั้ง (สามารถข้ามวันได้ เช่น 23:00 -
									01:00)
								</FormDescription>
								<FormMessage />
							</FormItem>
						)}
					/>
				</div>

				<FormField
					control={form.control}
					name="isActive"
					render={({ field }) => (
						<FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
							<div className="space-y-0.5">
								<FormLabel className="text-base">
									เปิดใช้งานตารางเวลานี้
								</FormLabel>
								<FormDescription>
									เมื่อเปิดใช้งาน ระบบจะปิดอัตโนมัติตามเวลาที่กำหนด
								</FormDescription>
							</div>
							<FormControl>
								<Switch
									checked={field.value}
									onCheckedChange={field.onChange}
								/>
							</FormControl>
						</FormItem>
					)}
				/>

				<div className="flex gap-4">
					<Button type="submit" disabled={isLoading}>
						{isLoading
							? 'กำลังบันทึก...'
							: initialData
								? 'บันทึกการแก้ไข'
								: 'สร้างตารางเวลา'}
					</Button>
					{onCancel && (
						<Button type="button" variant="outline" onClick={onCancel}>
							ยกเลิก
						</Button>
					)}
				</div>
			</form>
		</Form>
	)
}
